import { yupResolver } from '@hookform/resolvers/yup';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import {
  PAGE_DEFAULT,
  SORT_ATTRIBUTE_INVOICES_DEFAULT,
  SORT_ORDER_DEFAULT
} from 'constants/defaults';
import {
  InputMaybe,
  InvoicePage,
  InvoiceSortAttributeEnum,
  QueryInvoicesArgs,
  SortOrderEnum
} from 'gql/graphql';
import { usePathname, useSearchParams } from 'next/navigation';
import { useInvoice } from 'providers/InvoicesProviders/useInvoice';
import { useInvoices } from 'providers/InvoicesProviders/useInvoices';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { TGenericError } from 'types/TGenericError';
import { TScheme } from 'types/TScheme';
import { extractParam } from 'utils/paramsUtil';
import * as yup from 'yup';

export type FiltersInvoicesForm = Omit<QueryInvoicesArgs, 'page' | 'perPage' | 'skipPagination'>;

interface IUseSearchParamsInvoicesIn {
  currentInvoiceId: string | null;
}

interface IUseSearchParamsInvoicesOut {
  invoices?: InvoicePage;
  isLoading: boolean;
  isPending: boolean;
  isRetrieving: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  handleClear: () => void;
  control: Control<FiltersInvoicesForm>;
  filtersAdded: boolean;
  sortAttribute?: InputMaybe<InvoiceSortAttributeEnum>;
  sortOrder?: InputMaybe<SortOrderEnum>;
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<InvoicePage, TGenericError>>;
}

export default function useSearchParamsInvoices({
  currentInvoiceId
}: IUseSearchParamsInvoicesIn): IUseSearchParamsInvoicesOut {
  const schema = yup.object().shape({} as TScheme<FiltersInvoicesForm>);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [page, setPage] = useState(PAGE_DEFAULT);

  const {
    control,
    setValue,
    formState: { isDirty }
  } = useForm<FiltersInvoicesForm>({
    resolver: yupResolver(schema)
  });

  const pageInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT) as number,
    [searchParams]
  );

  const sortAttributeInitial = extractParam(
    searchParams,
    KeysEnum.SortAttribute,
    SORT_ATTRIBUTE_INVOICES_DEFAULT
  );

  const sortOrderInitial = extractParam(searchParams, KeysEnum.SortOrder, SORT_ORDER_DEFAULT);

  const sortAttribute = useWatch<FiltersInvoicesForm>({
    control,
    name: KeysEnum.SortAttribute,
    defaultValue: SORT_ATTRIBUTE_INVOICES_DEFAULT
  }) as InputMaybe<InvoiceSortAttributeEnum>;

  const sortOrder = useWatch<FiltersInvoicesForm>({
    control,
    name: KeysEnum.SortOrder,
    defaultValue: SORT_ORDER_DEFAULT
  }) as InputMaybe<SortOrderEnum>;

  const filtersAdded = useMemo(
    () =>
      [
        page !== PAGE_DEFAULT,
        sortOrderInitial !== SORT_ORDER_DEFAULT,
        sortAttributeInitial !== SORT_ATTRIBUTE_INVOICES_DEFAULT
      ].some(Boolean),
    [page, sortAttributeInitial, sortOrderInitial]
  );

  const input: QueryInvoicesArgs = {
    sortAttribute,
    sortOrder,
    page
  };

  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    isPending: isPendingInvoices,
    refetch
  } = useInvoices(input);
  const {
    data: invoiceData,
    isLoading: isLoadingInvoice,
    isPending: isPendingInvoice
  } = useInvoice({ id: String(currentInvoiceId) }, { enabled: !!currentInvoiceId });

  const invoices = useMemo(() => {
    const pathSegments = pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    const isDetailPage = /^\d+$/.test(lastSegment);

    if (currentInvoiceId && isDetailPage) {
      if (invoiceData?.id)
        return {
          nodes: [invoiceData],
          nodesCount: 1,
          pagesCount: 1
        };

      return {
        nodes: [],
        nodesCount: 0,
        pagesCount: 0
      };
    }

    return invoicesData;
  }, [currentInvoiceId, invoiceData, invoicesData, pathname]) as InvoicePage | undefined;

  const totalPages = invoices?.pagesCount ?? 0;

  const isLoading = isLoadingInvoices || (isLoadingInvoice && !!currentInvoiceId);
  const isPending = isPendingInvoices || (isPendingInvoice && !!currentInvoiceId);

  const isRetrieving = isLoading && page === 1;

  const resetFilters = useCallback(() => {
    setPage(pageInitial);
    setValue(KeysEnum.SortAttribute, sortAttributeInitial);
    setValue(KeysEnum.SortOrder, sortOrderInitial);
  }, [pageInitial, sortAttributeInitial, sortOrderInitial, setValue]);

  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      url.searchParams.delete(KeysEnum.Page);
      url.searchParams.delete(KeysEnum.SortAttribute);
      url.searchParams.delete(KeysEnum.SortOrder);

      resetFilters();

      window.history.replaceState({}, '', url.toString());
    }
  }, [resetFilters]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (sortOrder && sortAttribute)
        url.searchParams.set(KeysEnum.SortOrder, JSON.stringify(sortOrder));
      else url.searchParams.delete(KeysEnum.SortOrder);

      if (sortAttribute && sortOrder)
        url.searchParams.set(KeysEnum.SortAttribute, JSON.stringify(sortAttribute));
      else url.searchParams.delete(KeysEnum.SortAttribute);

      url.searchParams.delete(KeysEnum.Page);
      setPage([sortOrder && sortAttribute].some(Boolean) && isDirty ? PAGE_DEFAULT : pageInitial);

      window.history.replaceState({}, '', url.toString());
    }
  }, [pageInitial, sortOrder, sortAttribute, isDirty]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname]);

  return {
    invoices,
    isLoading,
    isPending,
    isRetrieving,
    page,
    setPage,
    totalPages,
    handleClear,
    control,
    filtersAdded,
    sortAttribute,
    sortOrder,
    refetch
  };
}
